import { Box, Grid, Paper, Stack } from "@mantine/core";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Printable, usePrintableContext } from "@components";
import { hasExtraocularMuscleFunction } from "@utils";
import {
  AntiSaccadesTestResult,
  OcularMotilityTestResult,
  SmoothPursuitTestResult,
  TestType,
} from "types/testTypes";
import CircularPursuitGraph from "../../common/CircularPursuitGraph";
import { MotilityMuscleReport } from "../../common/motility";
import OcularMotilityErrorCharts from "../../common/OcularMotilityErrorCharts";
import OcularMotilityMetricsView from "../../common/OcularMotilityMetrics";
import ClinicalSummaryCard from "../common/ClinicalSummaryCard";
import { ClinicalSummaryMetric } from "../utils/clinicalSummaryTypes";
import { readMotilityAnalysis } from "../utils/motilityPresentation";
import {
  interpretOcularMotilitySummary,
  reconstructOcularMotilityChartData,
} from "../utils/ocularMotilityInterpretation";
import AntiSaccadesTestEntry from "./AntiSaccadesTestEntry";
import SmoothPursuitTestEntry from "./SmoothPursuitTestEntry";

const formatMetricValue = (metric: ClinicalSummaryMetric) => {
  if (metric.value === null || metric.value === undefined || metric.value === "") {
    return "—";
  }

  if (typeof metric.value === "string") {
    return metric.value;
  }

  const formattedValue =
    typeof metric.decimals === "number" ? metric.value.toFixed(metric.decimals) : `${metric.value}`;
  return `${formattedValue}${metric.unit ?? ""}`;
};

function OcularMotilityTestEntry({ entry, header }: { entry; header?: ReactNode }) {
  const { t } = useTranslation();
  const { prettier } = usePrintableContext();
  const isAntiSaccades = entry?.testType === TestType.AntiSaccades;
  const printGroup = entry?.uuid ?? entry?._id ?? "ocular-motility";
  const isSmoothPursuit = entry?.testType === TestType.SmoothPursuit;
  const testResults =
    isAntiSaccades || isSmoothPursuit ? null : (entry as OcularMotilityTestResult | null);

  const chartData = useMemo(
    () => reconstructOcularMotilityChartData(testResults?.results),
    [testResults?.results],
  );

  const motilityAnalysis = readMotilityAnalysis(testResults?.results);

  const interpretation = useMemo(
    () =>
      isAntiSaccades || isSmoothPursuit
        ? null
        : interpretOcularMotilitySummary({
            chartData,
            metrics: testResults?.results ?? null,
            testType: testResults?.testType,
          }),
    [isAntiSaccades, isSmoothPursuit, chartData, testResults?.results, testResults?.testType],
  );

  // Anti-saccades renders its own Printable.Sections so its multi-page
  // content paginates cleanly inside bordered overview reports.
  if (isAntiSaccades) {
    return (
      <AntiSaccadesTestEntry
        entry={entry as AntiSaccadesTestResult}
        header={header}
        printGroup={printGroup}
      />
    );
  }

  // Smooth pursuit spans two pages, so it owns its sections. No muscle sheet: a
  // fused sweep of a ten degree circle measures pursuit, not muscle excursion.
  if (isSmoothPursuit) {
    return (
      <SmoothPursuitTestEntry
        entry={entry as SmoothPursuitTestResult}
        header={header}
        printGroup={printGroup}
      />
    );
  }

  const muscleReport =
    motilityAnalysis && hasExtraocularMuscleFunction(entry) ? (
      <MotilityMuscleReport analysis={motilityAnalysis} />
    ) : null;

  const body = (
    <Grid>
      {interpretation && (
        <Grid.Col span={12}>
          <ClinicalSummaryCard
            title={t(interpretation.titleKey)}
            explainer={t(interpretation.explainerKey)}
            riskLevel={interpretation.riskLevel}
            riskLabel={t(`extraTests.ocularMotility.summary.risk.${interpretation.riskLevel}`)}
            reason={t(interpretation.reasonKey)}
            keyFindingsLabel={t("extraTests.ocularMotility.summary.keyFindingsLabel")}
            keyFindings={interpretation.keyFindings}
            interpretationLabel={t("extraTests.ocularMotility.summary.howToInterpretLabel")}
            interpretationNotes={interpretation.interpretationNotes}
            metrics={interpretation.metrics.map((metric) => ({
              label: t(metric.labelKey),
              value: formatMetricValue(metric),
            }))}
          />
        </Grid.Col>
      )}

      <Grid.Col span={{ base: 12, lg: 8 }} className="om-result-graph-col">
        <Paper withBorder p="sm" radius="md">
          <Grid gutter="sm" className="om-result-split-graphs">
            <Grid.Col span={{ base: 12, sm: 6 }} className="om-result-split-graph-col">
              <Box style={{ maxWidth: 338, margin: "0 auto" }}>
                <CircularPursuitGraph
                  data={chartData}
                  visibleEye="left"
                  title={t("leftEye")}
                  compact
                  height={338}
                  showPaper={false}
                />
              </Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }} className="om-result-split-graph-col">
              <Box style={{ maxWidth: 338, margin: "0 auto" }}>
                <CircularPursuitGraph
                  data={chartData}
                  visibleEye="right"
                  title={t("rightEye")}
                  compact
                  height={338}
                  showPaper={false}
                />
              </Box>
            </Grid.Col>
          </Grid>
        </Paper>
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 4 }} className="om-result-metrics-col">
        {testResults?.results && (
          <OcularMotilityMetricsView
            metrics={testResults.results}
            testType={testResults?.testType}
            chartData={chartData}
          />
        )}
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 8 }} className="om-result-error-col">
        <OcularMotilityErrorCharts data={chartData} testType={testResults?.testType} />
      </Grid.Col>
      {muscleReport && (
        <Grid.Col span={{ base: 12, lg: 8 }} className="om-result-muscle-col">
          {muscleReport}
        </Grid.Col>
      )}
    </Grid>
  );

  // Beside the error chart, so XIH keeps to one page.
  return (
    <Printable.Section printGroup={printGroup}>
      <Stack>
        {header}
        <Box mx={prettier ? 0 : "xl"}>{body}</Box>
      </Stack>
    </Printable.Section>
  );
}

OcularMotilityTestEntry.selfSections = true;

export default OcularMotilityTestEntry;
